import { useEffect, useState } from 'react'
import { Bug, CheckCircle2, Mail, Phone, User } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { BugReportFileUploader } from '@/store/components/BugReportFileUploader'
import { useCheckoutFormStore } from '@/store/stores/checkoutFormStore'
import { submitBugReport } from '@/services/bugReports'
import { validateBugReportFiles } from '@/services/storage'
import { isFirebaseConfigured } from '@/lib/firebase'
import toast from 'react-hot-toast'

interface RaiseBugModalProps {
  open: boolean
  onClose: () => void
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const SUCCESS_MESSAGE =
  'Thank you for raising a bug, we truly appreciate your efforts. Your bug has been submitted successfully and is being reviewed by our team. If it gets approved by the review team, we will contact you via email for your discount coupon.'

export function RaiseBugModal({ open, onClose }: RaiseBugModalProps) {
  const fullName = useCheckoutFormStore((s) => s.fullName)
  const email = useCheckoutFormStore((s) => s.email)
  const phone = useCheckoutFormStore((s) => s.phone)
  const setFullName = useCheckoutFormStore((s) => s.setFullName)
  const setEmail = useCheckoutFormStore((s) => s.setEmail)
  const setPhone = useCheckoutFormStore((s) => s.setPhone)

  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (open) {
      setDescription('')
      setFiles([])
      setSubmitted(false)
    }
  }, [open])

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!fullName.trim() || fullName.trim().length < 2) {
      toast.error('Please enter your full name.')
      return
    }
    if (!isValidEmail(email.trim())) {
      toast.error('Please enter a valid email address.')
      return
    }
    if (phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number.')
      return
    }
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please describe the bug (at least 10 characters).')
      return
    }

    const fileError = validateBugReportFiles(files)
    if (fileError) {
      toast.error(fileError)
      return
    }

    if (!isFirebaseConfigured()) {
      toast.error('Bug report form is unavailable. Please try again later.')
      return
    }

    setSubmitting(true)
    try {
      await submitBugReport({
        fullName,
        email,
        phone,
        description,
        files,
      })
      setSubmitted(true)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to submit bug report.'
      const isPermission =
        message.toLowerCase().includes('permission') ||
        (err as { code?: string })?.code === 'permission-denied'
      toast.error(
        isPermission
          ? 'Could not submit the bug report. Please try again in a moment.'
          : message
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Modal open={open} onClose={handleClose} title="Bug Submitted" size="md">
        <div className="space-y-5 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
          <p className="text-sm leading-relaxed text-zinc-300">{SUCCESS_MESSAGE}</p>
          <Button type="button" onClick={handleClose} className="w-full">
            Close
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Raise a Bug & Win a Discount Coupon"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-zinc-400">
          Found an issue on our website? Report it below. Approved reports may
          qualify for a discount coupon — we will reach out via email.
        </p>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">
            Full name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              className="input-luxury pl-10"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              required
              maxLength={120}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-zinc-400">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input
                type="email"
                className="input-luxury pl-10"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                maxLength={200}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-zinc-400">
              Phone *
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <input
                type="tel"
                className="input-luxury pl-10"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Your phone number"
                autoComplete="tel"
                required
                maxLength={30}
              />
            </div>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">
            Bug explanation *
          </label>
          <div className="relative">
            <Bug className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
            <textarea
              className="input-luxury min-h-[120px] resize-y pl-10"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what went wrong, where you saw it, and steps to reproduce if possible"
              required
              maxLength={5000}
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">
            Attach image or video (optional)
          </label>
          <BugReportFileUploader
            files={files}
            onChange={setFiles}
            disabled={submitting}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={submitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button type="submit" loading={submitting} className="flex-1">
            Submit Bug Report
          </Button>
        </div>
      </form>
    </Modal>
  )
}
